import { clsx } from 'clsx'
import React, { forwardRef } from 'react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'

const styles = {
  base: [
    // Basic layout
    'relative isolate',
    // Sizing
    'w-full',
    // Typography
    'text-sm/6 text-gray-900',
    'dark:text-white',
    // Background colors
    'bg-white dark:bg-white/5',
    // Border
    'ring-1 ring-gray-300 dark:ring-white/10',
    // Shadow
    'shadow-sm',
    // Focus
    'focus:outline-none focus:ring-2 focus:ring-gray-500/20',
    // Disabled
    'disabled:bg-gray-50 disabled:text-gray-500 disabled:ring-gray-200',
    'dark:disabled:bg-white/5 dark:disabled:text-gray-400 dark:disabled:ring-white/10',
    // Select specific
    'appearance-none',
    'cursor-pointer',
  ],
  size: {
    sm: 'px-3 py-1.5 text-xs/5',
    md: 'px-3 py-2 text-sm/6',
    lg: 'px-4 py-2.5 text-sm/6',
    xl: 'px-4 py-3 text-base/7',
  },
  rounded: {
    none: 'rounded-none',
    sm: 'rounded',
    md: 'rounded-lg',
    lg: 'rounded-xl',
    full: 'rounded-full',
  },
}

type SelectProps = {
  className?: string
  size?: keyof typeof styles.size
  rounded?: keyof typeof styles.rounded
} & Omit<React.ComponentPropsWithoutRef<'select'>, 'size'>

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    className,
    size = 'md',
    rounded = 'md',
    ...props
  },
  ref
) {
  return (
    <div className="relative">
      <select
        {...props}
        className={clsx(
          styles.base,
          styles.size[size as keyof typeof styles.size],
          styles.rounded[rounded as keyof typeof styles.rounded],
          'pr-10',
          className
        )}
        ref={ref}
      />
      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
        <ChevronDownIcon className="h-4 w-4 text-gray-400" />
      </div>
    </div>
  )
})
